import React, { useState, FormEvent, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Suggest, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, AnchorButton, Button, Tabs, Tab, Collapse, HTMLSelect, HTMLTable, ControlGroup, Classes, MenuItem, Alert, Spinner } from "@blueprintjs/core";

import { IconNames } from "@blueprintjs/icons";

import { AxiosInstance } from 'axios';

import * as utils from "./utils";

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next';

import { Account, Currency, NumData, Environment, Voucher, VoucherItem, ForeignBalance, ForeignBalanceItem } from './Types'


interface VoucherViewProps {
    currentEnv: Environment,
    vch: Voucher,
    baseUrl?: string,
    token?: string | null,
    onNew?: () => void,
    onEdit?: () => void,
    onCopy?: () => void,
    onDelete?: (vch: Voucher) => void,
}

function VoucherView({ currentEnv = {} as Environment, vch = {} as Voucher, baseUrl = "", token = "", onNew = () => {}, onEdit = () => { }, onCopy = () => { }, onDelete = () => { } }: VoucherViewProps) {
    const { t, i18n } = useTranslation();
    const [isOpenDelete, setIsOpenDelete] = useState(false)

    if (!("voucherCode" in vch)) return null

    return (
        <div>
            {/*
            <Dialog icon="help" isOpen={isOpenDelete} canEscapeKeyClose={true} canOutsideClickClose={true} title="AC3x" onClose={() => setIsOpenDelete(false)}>
                <div className="DialogBody">
                    <div className="Spacing-V-16" />
                    <p>Delete voucher {vch.voucherCode}?</p>
                    <div className="Row-4">
                        <Button className="Col-3-1" icon="cross" onClick={() => setIsOpenDelete(false)}>No</Button>
                        <Button className="Col-4-1" icon="tick" intent="danger" onClick={() => {onDelete(vch); setIsOpenDelete(false)}}>DELETE</Button>
                    </div>
                </div>
            </Dialog>
            */}
            <Alert icon="trash" intent="danger" confirmButtonText={t('button_delete')} isOpen={isOpenDelete} onConfirm={() => {onDelete(vch); setIsOpenDelete(false)}} onCancel={() => setIsOpenDelete(false)} cancelButtonText={t('button_no')} canEscapeKeyCancel={true} canOutsideClickCancel={true}>
                <p>{t('confirm_delete_voucher', {vch})}</p>
            </Alert>
            <div className="Row">
                <Button className="Col-1-1" icon="add" onClick={() => onNew()}>{t('new')}</Button>
                <Button className="Col-2-1" icon="edit" onClick={() => onEdit()}>{t('edit')}</Button>
                <Button className="Col-3-1" icon="duplicate" onClick={() => onCopy()}>{t('make_copy')}</Button>
                {vch.type !== "" && <AnchorButton className="Col-4-1" icon="link" href={`#/${utils.journalBase(vch.type)}/detail/${vch.journalCode}/${vch.voucherCode}`}>{t(utils.journalBase(vch.type))}</AnchorButton>}
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('journal')}>
                    <InputGroup readOnly value={vch.journalCode} />
                </FormGroup>
                <FormGroup className="Col-2-1" label={t('voucher')}>
                    <InputGroup readOnly value={vch.voucherCode} />
                </FormGroup>
                <FormGroup className="Col-3-1" label={t('reference')}>
                    <InputGroup readOnly value={vch.reference} />
                </FormGroup>
                <FormGroup className="Col-4-1" label={t('date')}>
                    <InputGroup readOnly value={vch.txDate} />
                </FormGroup>
            </div>
            <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-Desc">{t('description')}</th>
                    {currentEnv.multiCurrency && <>
                    <th className="TCol-Currency"><div className="Align-R">{t('currency')}</div></th>
                    <th className="TCol-ExchangeRate"><div className="Align-R">{t('exchange_rate')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('fdr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('fcr')}</div></th>
                    </>}
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                </tr>
            </thead>
            <tbody>
                {vch.items.map((item, i) =>
                    <tr key={i}>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{item.acName}</td>
                        <td className="TCol-Desc">{item.description1}</td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Currency">{item.ccyCode}</td>
                        <td className="TCol-ExchangeRate"><div className="Align-R">{item.exRate}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(item.fdr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(item.fcr)}</div></td>
                        </>}
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(item.dr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(item.cr)}</div></td>
                    </tr>
                )}
            </tbody>
            <tfoot>
                {!currentEnv.multiCurrency && <tr>
                <td className="TCol-ACCode"></td>
                <td className="TCol-ACName"></td>
                <td className="TCol-Desc"></td>
                <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(vch.dr)}</b></div></td>
                <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(vch.cr)}</b></div></td>
                </tr>}

                {currentEnv.multiCurrency && Object.entries(vch.fbal).map((bal, i) => <tr>
                <td className="TCol-ACCode"></td>
                <td className="TCol-ACName"></td>
                <td className="TCol-Desc"></td>
                <td className="TCol-Currency"><b>{bal[0]}</b></td>
                <td className="TCol-ExchangeRate"></td>
                <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fdr)}</b></div></td>
                <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fcr)}</b></div></td>
                <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(vch.dr)}</b></div>}</td>
                <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(vch.cr)}</b></div>}</td>
                </tr>)}
            </tfoot>
            </HTMLTable>
            <div className="Spacing-V-16" />
            <div className="Row">
                <Button className="Col-1-1" icon="trash" onClick={() => setIsOpenDelete(!isOpenDelete)}>{t('delete')}</Button>
            </div>
        </div>
    )
}

interface VoucherEditProps {
    currentEnv: Environment,
    vch?: Voucher,
    ccyList?: Currency[],
    acList?: Account[],
    numList?: NumData[],
    isNew?: boolean,
    onSave: (vch: Voucher, saveAndNew: boolean) => Promise<boolean>,
    onCancel: () => void,
}

// onChange={utils.handleStringChange(s => setAc(prevState => Object.assign(prevState, {acName1: s})))}
function VoucherEdit({ currentEnv = {} as Environment, vch = {} as Voucher, ccyList = [] as Currency[], acList = [] as Account[], numList = [] as NumData[], isNew = false, onSave, onCancel }: VoucherEditProps) {
    const { t, i18n } = useTranslation();
    const { register, handleSubmit, reset, setValue, getValues } = useForm<Voucher>()

    const defaultItems = "voucherCode" in vch ? vch.items : [{} as VoucherItem, {} as VoucherItem]
    //const [defaultJournalCode, setDefaultJournalCode] = useState("journalCode" in vch ? vch.journalCode : "")
    //const [defaultVoucherCode, setDefaultVoucherCode] = useState("voucherCode" in vch ? vch.voucherCode : "")
    const [items, setItems] = useState(defaultItems)
    console.log("setItems to defaultItems", vch.journalCode, vch.voucherCode)
    const [totalDr, setTotalDr] = useState(0)
    const [totalCr, setTotalCr] = useState(0)
    const [totalFBal, setTotalFBal] = useState({} as ForeignBalance)

    const onFormChange = () => recalcTotals()
    const recalcTotals = () => {
        //console.log("onFormChange")
        let sumDr = 0, sumCr = 0, fsum = {} as ForeignBalance
        for (let i = 0; i < items.length; i++) {
            let ccyCode = getValues(`items[${i}].ccyCode`) as string || ""
            if (!(ccyCode in fsum)) {
                fsum[ccyCode] = {fdr: 0, fcr: 0} as ForeignBalanceItem
                console.log(ccyCode, "fcr", fsum[ccyCode].fcr)
            }
            fsum[ccyCode].fdr += utils.StringToNum(getValues(`items[${i}].fdr`))
            fsum[ccyCode].fcr += utils.StringToNum(getValues(`items[${i}].fcr`))
            sumDr += utils.StringToNum(getValues(`items[${i}].dr`))
            sumCr += utils.StringToNum(getValues(`items[${i}].cr`))
        }
        setTotalDr(sumDr)
        setTotalCr(sumCr)
        setTotalFBal(fsum)
    }

    useEffect(() => {
        console.log("useEffect [items]")
        console.log(items)
    }, [items])

    useEffect(() => {
        // Call reset to set default values if vch is passed
        //console.log("useEffect vch, reset, setValue, isNew")
        console.log("useEffect []")

        if ("voucherCode" in vch) {
            if (isNew) {
                reset({...vch, ...{voucherCode: "", txDate: utils.formatDate(new Date())}})
            } else {
                reset(vch)
            }
        }
        if (isNew) {
            setValue("txDate", utils.formatDate(new Date()))
            // setValue("type", "")
        }
        recalcTotals()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    //}, [vch, reset, setValue, isNew])

    useEffect(() => {
        // Suggest don't have ref to use with react-hook-form, so register it manually and call setValue when item is selected
        // New items are register in onAdd
        console.log("useEffect [register]")
        // register({ name: "ccyCode" })
        register({ name: "journalCode" })
        // register({ name: "type" })
        items.map((item, i) => {
            register({ name: `items[${i}].acCode`})
            register({ name: `items[${i}].ccyCode`})
            // return null
        })
        console.log("register", "ccyCode", "journalCode", "items")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [register])

    if (!isNew && !("voucherCode" in vch)) return null

    //const onSubmitAndNew = (data: Voucher) => preSave(data, true)
    //const onSubmit = (data: Voucher) => preSave(data, false)
    //const preSave = async (data: Voucher, saveAndNew: boolean = false) => {
    const onSubmit = async (data: Voucher) => {
        data.dr = 0
        data.cr = 0
        console.log(Object.assign(data))
        for (let i = data.items.length-1; i >= 0; i--) {
            //console.log(i, data.items[i] && data.items[i].acCode)
            if (data.items[i] === undefined ||
                data.items[i].acCode === "" ||
                (data.items[i].acCode !== "C" && (data.items[i].dr - data.items[i].cr) === 0) ||
                (data.items[i].acCode === "C" && data.items[i].description1 === ""  && (data.items[i].dr - data.items[i].cr) === 0)) {
                //console.log("deleting", i)
                data.items.splice(i, 1)
            } else {
                data.dr += Number(data.items[i].dr)
                data.cr += Number(data.items[i].cr)
            }
        }
        let saveAndNew = isNew
        if (await onSave(data, saveAndNew) && saveAndNew) {
            //console.log("saveAndNew resetting form")
            setItems([{} as VoucherItem, {} as VoucherItem])
            reset({
                journalCode: data.journalCode,
                voucherCode: "",
                reference: "",
                txDate: utils.formatDate(new Date()),
                items: [{acName: ""}, {acName: ""}],
            })
            recalcTotals()
            document.getElementsByName(`journalCode`)[0].focus()
        }
    }

    const onAdd = () => {
        register({ name: `items[${items.length}].acCode`})
        register({ name: `items[${items.length}].ccyCode`})
        // console.log("register", { name: `items[${items.length}].acCode`})
        // console.log("register", { name: `items[${items.length}].ccyCode`})
        setItems(items.concat({} as VoucherItem))
    }

    const acRenderer: ItemRenderer<Account> = (ac, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        //const text = `${ac.acCode} - ${ac.acName1}`;
        const text = `${ac.acCode} ${ac.acName1.length > 0 || ac.acName2.length > 0 ? '-' : ''} ${ac.acName1+ac.acName2}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={ac.acCode}
                key={ac.acCode}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const acPredicate: ItemPredicate<Account> = (query, ac, _index, exactMatch) => {
        const normalizedText = `${ac.acCode} - ${ac.acName1+ac.acName2}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const acValueRenderer = (ac: Account) => {
        return ac.acCode
    }

    const ccyRenderer: ItemRenderer<Currency> = (ccy, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        //const text = `${ac.acCode} - ${ac.acName1}`;
        const text = `${ccy.ccyCode} - ${ccy.ccyName}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={ccy.ccyCode}
                key={ccy.ccyCode}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const ccyPredicate: ItemPredicate<Currency> = (query, ccy, _index, exactMatch) => {
        const normalizedText = `${ccy.ccyCode} - ${ccy.ccyName}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const ccyValueRenderer = (ccy: Currency) => {
        console.log(ccy)
        return ccy.ccyCode
    }


    const numRenderer: ItemRenderer<NumData> = (num, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        const text = `${num.group}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={num.group}
                key={num.group}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const numPredicate: ItemPredicate<NumData> = (query, num, _index, exactMatch) => {
        const normalizedText = `${num.group}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const numValueRenderer = (num: NumData) => {
        return num.group
    }

    const createNumDataFromQuery = (query: string) => {
        return {type: "J", group: query.toUpperCase(), last: 0} as NumData
    }

    const createNumDataRenderer = (
        query: string,
        active: boolean,
        handleClick: React.MouseEventHandler<HTMLElement>,
    ) => (
        <MenuItem
            icon="add"
            text={`Create "${query}"`}
            active={active}
            onClick={handleClick}
            shouldDismissPopover={false}
        />
    );

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const handleHotkey = (e: React.KeyboardEvent<HTMLFormElement>) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // ctrl + s
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
            handleSubmit(onSubmit)()
            /*
            if (isNew) {
                handleSubmit(onSubmitAndNew)()
            } else {
                handleSubmit(onSubmit)()
            }
            */
        }
    }

    /*<form onSubmit={handleSubmit(onSubmit)}>*/
    return (
        <form onKeyDown={handleHotkey} onChange={onFormChange}>
            <div className="Row">
                <Button className="Col-1-1" icon="cross" onClick={() => onCancel && onCancel()}>{t('cancel')}</Button>
                {/*!isNew && <Button className="Col-2-1" icon="tick" onClick={handleSubmit(onSubmit)}>{t('save')}</Button>*/}
                {/*isNew && <Button className="Col-2-1" icon="add" onClick={handleSubmit(onSubmitAndNew)}>{t('save')}</Button>*/}
                <Button className="Col-2-1" icon={isNew?"add":"tick"} onClick={handleSubmit(onSubmit)}>{t('save')}</Button>
                {/*<Button className="Col-2-1" icon="undo" onClick={() => reset(vch)}>Reset</Button>*/}
                {/*<Button className="Col-3-1" icon="tick" type="submit">Save</Button>*/}
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('journal')} labelFor="inputJournalCode">
                    {/*<InputGroup readOnly={!isNew} id="inputJournalCode" name="journalCode" inputRef={register} />*/}
                    {isNew && <Suggest
                        inputProps={{ placeholder: "", name: "journalCode" }}
                        items={numList}
                        itemRenderer={numRenderer}
                        itemPredicate={numPredicate}
                        inputValueRenderer={numValueRenderer}
                        createNewItemFromQuery={createNumDataFromQuery}
                        createNewItemRenderer={createNumDataRenderer}
                        resetOnClose={true}
                        onItemSelect={(num) => {
                            setValue(`journalCode`, num.group)
                            document.getElementsByName(`voucherCode`)[0].focus()
                        }}
                    />}
                    {!isNew && <InputGroup readOnly defaultValue={vch.journalCode} />}
                </FormGroup>
                <FormGroup className="Col-2-1" label={t('voucher')} labelFor="inputVoucherCode">
                    <InputGroup readOnly={!isNew} id="inputVoucherCode" name="voucherCode" placeholder={t('auto_generated')} defaultValue={vch.voucherCode} inputRef={register} />
                </FormGroup>
                <FormGroup className="Col-3-1" label={t('reference')} labelFor="inputReference">
                    <InputGroup id="inputReference" name="reference" inputRef={register} />
                </FormGroup>
                <FormGroup className="Col-4-1" label={t('date')} labelFor="inputTxDate">
                    <InputGroup id="inputTxDate" name="txDate" type="date" inputRef={register} />
                </FormGroup>
                <Button className="Col-5-1 Button-LabeledFormGroup" icon="add" onClick={onAdd}>{t('add_entry')}</Button>
            </div>
            <HTMLTable striped interactive condensed>
            <thead>
                <th className="TCol-ACCode">{t('account')}</th>
                <th className="TCol-ACName">{t('account_name')}</th>
                <th className="TCol-Desc">{t('description')}</th>
                {currentEnv.multiCurrency && <>
                <th className="TCol-Currency"><div className="Align-R">{t('currency')}</div></th>
                <th className="TCol-ExchangeRate"><div className="Align-R">{t('exchange_rate')}</div></th>
                <th className="TCol-ACBalance"><div className="Align-R">{t('fdr')}</div></th>
                <th className="TCol-ACBalance"><div className="Align-R">{t('fcr')}</div></th>
                </>}
                <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
            </thead>
            <tbody>
                {items.map((item, i) =>
                    <tr>
                        {/*<td className="TCol-ACCode"><InputGroup id="inputAcCode" name={`items[${i}].acCode`} inputRef={register} /></td>*/}
                        <td className="TCol-ACCode">
                            <Suggest
                                inputProps={{ placeholder: "" }}
                                //defaultSelectedItem={acList.find((ac) => ac.acCode === item.acCode)}
                                selectedItem={{acCode: item.acCode} as Account}
                                items={acList}
                                itemRenderer={acRenderer}
                                itemPredicate={acPredicate}
                                inputValueRenderer={acValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(ac) => {
                                    // update value in react-form for submission
                                    setValue(`items[${i}].acCode`, ac.acCode)
                                    //console.log("setValue", `items[${i}].acCode`, ac.acCode)

                                    // update items for UI updating
                                    let newItems = Array.from(items)
                                    newItems[i].acCode = ac.acCode
                                    newItems[i].acName = ac.acName1+ac.acName2
                                    //console.log("newItems", newItems)
                                    setItems(newItems)

                                    document.getElementsByName(`items[${i}].description1`)[0].focus()
                                }}
                            />
                        </td>
                        <td className="TCol-ACName"><InputGroup readOnly id="inputAcName" name={`items[${i}].acName`} value={items[i].acName} inputRef={register} /></td>
                        {/*<td className="TCol-ACName"><InputGroup readOnly id="inputAcName" name={`items[${i}].acName`} inputRef={register} /></td>*/}
                        <td className="TCol-Desc">
                            <InputGroup id="inputDesc" name={`items[${i}].description1`} inputRef={register}
                                onKeyDown={
                                    (e: React.KeyboardEvent<HTMLInputElement>) => {
                                        // ctrl + shift + " (Excel copy above cell)
                                        if (e.ctrlKey && e.shiftKey && e.keyCode === 222) {
                                            e.preventDefault()
                                            setValue(`items[${i}].description1`, getValues(`items[${i-1}].description1`))
                                        }
                                    }
                                }
                                rightElement={
                                    i > 0 ? <Button icon="chevron-up" minimal onClick={() => {setValue(`items[${i}].description1`, getValues(`items[${i-1}].description1`))}}></Button> : undefined
                                }
                            />
                        </td>
                        {currentEnv.multiCurrency && <>
                        {/* <td className="TCol-Currency"><InputGroup id="inputCCY" name={`items[${i}].ccyCode`} inputRef={register} /></td> */}
                        <td className="TCol-Currency">
                            <Suggest
                                inputProps={{ placeholder: "" }}
                                //defaultSelectedItem={acList.find((ac) => ac.acCode === item.acCode)}
                                selectedItem={{ccyCode: item.ccyCode} as Currency}
                                items={ccyList}
                                itemRenderer={ccyRenderer}
                                itemPredicate={ccyPredicate}
                                inputValueRenderer={ccyValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(ccy) => {
                                    // update value in react-form for submission
                                    setValue(`items[${i}].ccyCode`, ccy.ccyCode)
                                    console.log("setValue", `items[${i}].ccyCode`, ccy.ccyCode)

                                    // update items for UI updating
                                    let newItems = Array.from(items)
                                    newItems[i].ccyCode = ccy.ccyCode
                                    //console.log("newItems", newItems)
                                    setItems(newItems)
                                    
                                    document.getElementsByName(`items[${i}].exRate`)[0].focus()
                                    recalcTotals()
                                }}
                            />
                        </td>
                        <td className="TCol-ExchangeRate"><InputGroup id="inputExRate" name={`items[${i}].exRate`} inputRef={register} /></td>
                        <td className="TCol-ACBalance"><InputGroup id="inputFDr" name={`items[${i}].fdr`} type="number" inputRef={register} /></td>
                        <td className="TCol-ACBalance"><InputGroup id="inputFCr" name={`items[${i}].fcr`} type="number" inputRef={register} /></td>
                        </>}
                        <td className="TCol-ACBalance"><InputGroup id="inputDr" name={`items[${i}].dr`} type="number" inputRef={register} /></td>
                        <td className="TCol-ACBalance"><InputGroup id="inputCr" name={`items[${i}].cr`} type="number" inputRef={register} /></td>

                    </tr>
                )}
            </tbody>
            <tfoot>
                {!currentEnv.multiCurrency && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-Desc"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totalDr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totalCr)}</b></div></td>
                </tr>}
                {currentEnv.multiCurrency && Object.entries(totalFBal).map((bal, i) => <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-Desc"></td>
                    <td className="TCol-Currency"><b>{bal[0]}</b></td>
                    <td className="TCol-ExchangeRate"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fdr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fcr)}</b></div></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totalDr)}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totalCr)}</b></div>}</td>
                </tr>)}
            </tfoot>
            </HTMLTable>
        </form>
    )
}

interface VoucherMainProps {
    svc: AxiosInstance,
    currentEnv: Environment,
}

/*
const toaster = Toaster.create({
    position: Position.TOP,
})
*/

function VoucherMain({ svc, currentEnv = {} as Environment }: VoucherMainProps) {
    let { tabId: defaultTabId = "list", journalCode: defaultJournalCode, voucherCode: defaultVoucherCode }: { tabId: string, journalCode: string, voucherCode: string } = useParams()
    const { t, i18n } = useTranslation();
    const history = useHistory()

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState([] as Voucher[])

    const [ccyList, setCcyList] = useState([] as Currency[])
    const [acList, setAcList] = useState([] as Account[])
    const [numList, setNumList] = useState([] as NumData[])

    const [searchKey, setSearchKey] = useState("journalCode")
    const [searchValue, setSearchValue] = useState("")
    const [searchValue2, setSearchValue2] = useState("")

    const [showFilters, setShowFilters] = useState(true)

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    const [searchFrom, setSearchFrom] = useState(utils.formatDate(new Date(y, m-1, 1)))
    const [searchTo, setSearchTo] = useState(utils.formatDate(date))

    const [searchReference, setSearchReference] = useState("")
    const [searchDesc, setSearchDesc] = useState("")

    const [searchLimit, setSearchLimit] = useState(0)
    const [searchBackward, setSearchBackward] = useState(false)

    const [selectedTabId, setSelectedTabId] = useState("list")
    const [lastTabId, setLastTabId] = useState("list")
    const [currentVch, setCurrentVoucher] = useState({} as Voucher)

    const [currentMode, setCurrentMode] = useState("show")

    useEffect(() => {
        console.log("useEffect [defaultJournalCode, defaultVoucherCode]")
        if ("envId" in currentEnv && defaultJournalCode && defaultVoucherCode) {
            console.log("show", defaultJournalCode, defaultVoucherCode);
            (async () => {
                try {
                    const res = await svc.post('/voucherlist',
                        {
                            by: "journalCode",
                            start: defaultJournalCode,
                            voucherCode: defaultVoucherCode,
                            limit: 1,
                        });
        
                    setData(res.data.data)
                    if (res.data.data.length > 0) {
                        setCurrentVoucher(res.data.data[0])
                    } else {
                        utils.showError(t('no_record'))
                    }
                    //changeTab("list")
                } catch (error) {
                    utils.showError(error)
                }
            })()
        } else {
            //console.log("not show", defaultJournalCode, defaultVoucherCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultJournalCode, defaultVoucherCode, svc, currentEnv])

    useEffect(() => {
        setSelectedTabId(defaultTabId)
    }, [defaultTabId])

    const changeTab = (tabId: string) => {
        history.push(`/vouchers/${tabId}`)
        setSelectedTabId(tabId)
    }

    /*
    useEffect(() => {
        setSearchFrom(utils.formatDate(new Date(currentEnv.startOfYear)))
        setSearchTo(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    }, [currentEnv])
    */

    const refreshNumList = async () => {
        const res = await svc.post('/numlist',
            {
                type: "J",
            });
        setNumList(res.data.data)
    }

    useEffect(() => {
        console.log("useEffect [svc, currentEnv]");
        (async () => {
            try {
                /*
                {
                    const res = await svc.post('/getdates',
                        {});
                    setSearchFrom(res.data.data.startOfYear)
                    setSearchTo(utils.formatDate(new Date()))
                }
                */
                if ("envId" in currentEnv) {
                    refreshNumList()
                    /*
                    const res = await svc.post('/numlist',
                        {
                            type: "J",
                        });
                    setNumList(res.data.data)
                    */
                }
                {
                    const res = await svc.post('/currencylist',
                        {
                            by: "ccyCode",
                            start: "",
                            limit: 0,
                            backward: false,
                            filters: {
                                ccyCode: "",
                            }
                        });
                    setCcyList(res.data.data)
                }
                if ("envId" in currentEnv) {
                    const res = await svc.post('/accountlist',
                        {
                            by: "acCode",
                            start: "",
                            limit: 0,
                            backward: false,
                            filters: {
                                isDetailOnly: true,
                            }
                        });

                    setAcList([{acCode: "C", acName1:"", acName2:""} as Account].concat(res.data.data))
                    //setAcList(res.data.data)
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svc, currentEnv])

    /*
    useEffect(() => {
        (async () => {
            try {
                const res = await svc.post('/currencylist',
                    {
                        by: "ccyCode",
                        start: "",
                        limit: 0,
                        backward: false,
                        filters: {
                            ccyCode: "",
                        }
                    });
                setCcyList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }, [svc])
    */

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/voucherlist',
                {
                    by: searchKey,
                    start: searchValue,
                    voucherCode: searchValue2,
                    limit: searchLimit,
                    backward: searchBackward,
                    filters: {
                        from: searchFrom,
                        to: searchTo,
                        reference: searchReference,
                        desc: searchDesc,
                    }
                });

            setIsLoading(false)
            setData(res.data.data)
            if (res.data.data.length > 0) {
                setCurrentVoucher(res.data.data[0])
            } else {
                utils.showError(t('no_record'))
            }
            //changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    const editOnSave = async (vch: Voucher) => {
        try {
            const res = await svc.post('/voucherupdate', vch)
            utils.showSuccess(t('saved', {key: res.data.data.journalCode+' '+res.data.data.voucherCode}))
            for (let i = 0; i < data.length; i++) {
                if (data[i].journalCode === res.data.data.journalCode && data[i].voucherCode === res.data.data.voucherCode) {
                    data[i] = { ...data[i], ...res.data.data }
                    setData(data)
                    break
                }
            }
            setCurrentVoucher(res.data.data)
            setCurrentMode("show")
            refreshNumList()
            return true
        } catch (error) {
            utils.showError(error)
            return false
        }
    }

    const newOnSave = async (vch: Voucher, saveAndNew: boolean = false) => {
        try {
            console.log("newOnSave", vch)
            const res = await svc.post('/voucheradd', vch)
            utils.showSuccess(t('saved', {key: res.data.data.journalCode+' '+res.data.data.voucherCode}))
            setCurrentVoucher(res.data.data)
            if (!saveAndNew) setCurrentMode("show")
            setData(data.concat(res.data.data))
            refreshNumList()
            return true
        } catch (error) {
            utils.showError(error)
            return false
        }
    }

    const viewOnDelete = async (vch: Voucher) => {
        try {
            //console.log("deleting voucher:", vch)
            const res = await svc.post('/voucherdelete', vch)
            utils.showSuccess(t('deleted'))
            setData(data.filter(v => !(v.journalCode === res.data.journalCode && v.voucherCode === res.data.voucherCode)))
            setCurrentVoucher({} as Voucher)
            changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    // currentMode = show
    let detail = <VoucherView currentEnv={currentEnv} vch={currentVch} baseUrl={svc.defaults.baseURL} token={localStorage.getItem('token')} onNew={() => {setLastTabId(selectedTabId); setCurrentMode("new")}} onEdit={() => setCurrentMode("edit")} onCopy={() => setCurrentMode("copy")} onDelete={viewOnDelete}></VoucherView>
    switch (currentMode) {
        case "edit":
            detail = <VoucherEdit currentEnv={currentEnv} vch={currentVch} ccyList={ccyList} acList={acList} numList={numList} onSave={editOnSave} onCancel={() => setCurrentMode("show")}></VoucherEdit>
            break
        case "new":
            detail = <VoucherEdit currentEnv={currentEnv} isNew ccyList={ccyList} acList={acList} numList={numList} onSave={newOnSave} onCancel={() => { setCurrentMode("show"); changeTab(lastTabId) }}></VoucherEdit>
            break
        case "copy":
            detail = <VoucherEdit currentEnv={currentEnv} isNew vch={currentVch} ccyList={ccyList} acList={acList} numList={numList} onSave={newOnSave} onCancel={() => { setCurrentMode("show") }}></VoucherEdit>
            break
    }

    let list = <>
        <div className="Row">
            <Button className="Col-1-1" icon="add" onClick={() => { setLastTabId(selectedTabId); setCurrentMode("new"); changeTab("detail") }}>{t('new')}</Button>
        </div>
        <div className="Spacing-V-16" />
        {isLoading && <Spinner></Spinner>}
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-Journal">{t('journal')}</th>
                    <th className="TCol-Voucher">{t('voucher')}</th>
                    <th className="TCol-Reference">{t('reference')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-Desc">{t('description')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data.map((vch, i) =>
                    <tr key={i} onDoubleClick={() => { changeTab("detail") }}>
                        <td className="TCol-Journal" onClick={() => { setCurrentVoucher(vch) }}>{vch.journalCode}</td>
                        {/*<td className="TCol-Voucher" onClick={() => { setCurrentVoucher(vch) }}>{vch.voucherCode}</td>*/}
                        <td className="TCol-Voucher" onClick={() => { setCurrentVoucher(vch) }}><a target="_blank" href={`#/vouchers/detail/${vch.journalCode}/${vch.voucherCode}`}>{vch.voucherCode}</a></td>
                        <td className="TCol-Reference" onClick={() => { setCurrentVoucher(vch) }}>{vch.reference}</td>
                        <td className="TCol-Date" onClick={() => { setCurrentVoucher(vch) }}>{vch.txDate}</td>
                        <td className="TCol-Desc" onClick={() => { setCurrentVoucher(vch) }}>{vch.description}</td>
                        <td className="TCol-ACBalance" onClick={() => { setCurrentVoucher(vch) }}><div className="Align-R">{utils.formatMoney(vch.dr)}</div></td>
                        <td className="TCol-ACBalance" onClick={() => { setCurrentVoucher(vch) }}><div className="Align-R">{utils.formatMoney(vch.cr)}</div></td>
                    </tr>
                )}
            </tbody>
        </HTMLTable>
    </>

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('vouchers')}</div>
            <div className="Spacing-V-16"></div>
            <form onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-3" label={t('search')} labelFor="inputSearchValue">
                        <ControlGroup fill>
                            <HTMLSelect className={Classes.FIXED} value={searchKey} onChange={utils.handleStringChange(s => setSearchKey(s))}>
                                <option value="voucherCode">{t('journal')}</option>
                            </HTMLSelect>
                            <InputGroup
                                id="inputSearchValue"
                                placeholder={t('journal')}
                                value={searchValue}
                                onChange={utils.handleStringChange(s => setSearchValue(s))}
                                leftIcon="search"
                            />
                            <InputGroup
                                id="inputSearchValue2"
                                placeholder={t('voucher')}
                                value={searchValue2}
                                onChange={utils.handleStringChange(s => setSearchValue2(s))}
                                rightElement={<Button icon={IconNames.ARROW_RIGHT} minimal type="submit"></Button>}
                            />
                        </ControlGroup>
                    </FormGroup>
                    {/*<Checkbox className="Col-4-1 Checkbox-LabeledFormGroup" label="Backward" defaultChecked={searchBackward} onChange={utils.handleBooleanChange(v => setSearchBackward(v))} />*/}
                    {/*<Button className="Col-5-1 Button-LabeledFormGroup" icon="filter" onClick={() => { setShowFilters(!showFilters) }} >Filters</Button>*/}
                </div>
                <Collapse isOpen={showFilters}>
                    <div className="Row">
                        <FormGroup className="Col-1-1" label={t('from')} labelFor="searchFrom">
                            <InputGroup id="searchFrom" type="date" value={searchFrom} onChange={utils.handleStringChange(s => setSearchFrom(s))} />
                        </FormGroup>
                        <FormGroup className="Col-2-1" label={t('to')} labelFor="searchTo">
                            <InputGroup id="searchTo" type="date" value={searchTo} onChange={utils.handleStringChange(s => setSearchTo(s))} />
                        </FormGroup>
                        <FormGroup className="Col-3-1" label={t('reference')} labelFor="searchReference">
                            <InputGroup id="searchReference" value={searchReference} onChange={utils.handleStringChange(s => setSearchReference(s))} />
                        </FormGroup>
                        {/*
                        <div className="Col-4-2 Col-H">
                            <FormGroup label="Limit" labelFor="searchLimit" >
                                <InputGroup className="Input-XS" id="searchLimit" value={searchLimit.toString()} onChange={utils.handleNumberChange(n => setSearchLimit(n))} />
                            </FormGroup>
                        </div>
                        */}
                    </div>
                    <div className="Row">
                        <FormGroup className="Col-1-3" label={t('description')} labelFor="searchDesc">
                            <InputGroup id="searchDesc" value={searchDesc} onChange={utils.handleStringChange(s => setSearchDesc(s))} />
                        </FormGroup>
                    </div>
                </Collapse>
            </form>
            <Tabs onChange={(newTabId) => changeTab(newTabId as string)} selectedTabId={selectedTabId}>
                <Tab id="list" title={t('list')} panel={list} />
                <Tab id="detail" title={t('detail')} panel={detail} />
            </Tabs>
        </>
    );
}

export default VoucherMain